import { useParams } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  OrderDetailsProvider,
  useOrderDetails,
} from "../../context/OrderDetailContext";
import { CustomerCard } from "../../components/cards/CustomerCard";
import { PaymentDetailsCard } from "../../components/cards/PaymentDetailsCard";
import { ShippingCard } from "../../components/cards/ShippingCard";
import { getOrderDetails, retryShopifyOrder } from "../../services/orders/orderAxios";
import { ProductDetail } from "../../components/cards/ProductDetail";
import { TopNavBarComponent } from "../../components/navbar/TopNavBarComponent";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../context/AuthContext";
import { IntegrationCardInfo } from "../../components/cards/IntegrationCardInfo";
import { DropiState, ShopifyState } from "../SalesPage/SalesPageInterface";
import { dictionary } from "../../utils/dictionary";
import { CustomerFieldsCard } from "../../components/cards/CustomerFieldsCard";
import { handleApi } from "../../utils/handleApi";
import { ConfirmationInfo, getConfirmationStatuses } from "../../services/whatsapp/whatsappService";
import { decodeTokenService } from "../../services/decodeTokenService";
import { formatCurrency, formatDateInZone, getCurrencySymbol } from "../../utils/formatters";
import { DropiRetryCard } from "../../components/cards/DropiRetryCard";
import { ChevronDown, RefreshCw } from "lucide-react";
import { toast } from "react-hot-toast";

const SHOPIFY_STATUS_LABEL: Record<string, string> = {
  [ShopifyState.PENDING]: "Pendiente por enviar",
  [ShopifyState.PENDING_RATE]: "Pendiente por tasa USD",
  [ShopifyState.RETRYING]: "Reintentando",
  [ShopifyState.SUCCESS]: "Sincronizada",
  [ShopifyState.ERROR_RETRYABLE]: "Error temporal",
  [ShopifyState.ERROR_FINAL]: "Revisión manual",
  [ShopifyState.ERROR]: "Error",
};

const ShopifySyncCard = ({
  timezone,
  onRetryComplete,
}: {
  timezone: string;
  onRetryComplete: () => void;
}) => {
  const { orderDetails } = useOrderDetails();
  const [isRetrying, setIsRetrying] = useState(false);
  const [showSyncDetails, setShowSyncDetails] = useState(false);
  const state = orderDetails?.shopify_integration_state;

  if (!orderDetails || !state) return null;

  const originalCurrency = orderDetails.shopify_original_currency || orderDetails.currency_code || "COP";
  const originalSymbol = getCurrencySymbol(originalCurrency);
  const isUsdConversion = orderDetails.shopify_currency_mode === "USD_CONVERSION";
  const sentCurrency = orderDetails.shopify_currency_sent || (isUsdConversion ? "USD" : originalCurrency);
  const sentSymbol = sentCurrency === "USD" ? "US$" : getCurrencySymbol(sentCurrency);
  const hasAppliedUsdRate =
    isUsdConversion &&
    state === ShopifyState.SUCCESS &&
    orderDetails.shopify_rate_used != null &&
    orderDetails.shopify_total_sent != null;
  const isPending = state === ShopifyState.PENDING || state === ShopifyState.PENDING_RATE || state === ShopifyState.RETRYING;
  const isError = state === ShopifyState.ERROR_RETRYABLE || state === ShopifyState.ERROR_FINAL || state === ShopifyState.ERROR;
  const canRetry = isError || state === ShopifyState.PENDING_RATE;
  const statusClass = state === ShopifyState.SUCCESS
    ? "bg-green-50 text-green-700"
    : isError
      ? "bg-red-50 text-red-700"
      : "bg-yellow-50 text-yellow-700";

  const description = (() => {
    if (state === ShopifyState.PENDING_RATE) {
      return "La orden está creada en Fluxi, pero Shopify espera la tasa de la fecha de la orden.";
    }
    if (state === ShopifyState.ERROR_RETRYABLE) {
      return "Fluxi volverá a intentar la sincronización automáticamente.";
    }
    if (state === ShopifyState.ERROR_FINAL) {
      return "Esta orden necesita revisión manual antes de volver a enviarse a Shopify.";
    }
    if (state === ShopifyState.SUCCESS) {
      return "La orden ya fue enviada a Shopify con la configuración guardada.";
    }
    return isPending ? "La orden está en cola para sincronizarse con Shopify." : "Shopify no pudo recibir esta orden.";
  })();

  const handleRetryShopify = async () => {
    if (isRetrying || !orderDetails?.id) return;
    setIsRetrying(true);
    const { res, error } = await handleApi(retryShopifyOrder(orderDetails.id), false);
    setIsRetrying(false);

    if (error) {
      toast.error("No se pudo reintentar la sincronización con Shopify.");
      return;
    }

    if (res?.accepted) {
      toast.success("Reintento Shopify encolado.");
    } else if (res?.success) {
      toast.success("Orden sincronizada con Shopify.");
    } else {
      toast("Reintento Shopify ejecutado. Revisa el estado actualizado.");
    }
    onRetryComplete();
  };

  const syncUpdatedAt = orderDetails.updated_at || orderDetails.created_at;

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Shopify</h2>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
          {SHOPIFY_STATUS_LABEL[state] || state}
        </span>
      </div>

      <button
        type="button"
        onClick={() => setShowSyncDetails((value) => !value)}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
      >
        {showSyncDetails ? "Ocultar detalle de sincronización" : "Ver detalle de sincronización"}
        <ChevronDown className={`h-4 w-4 transition-transform ${showSyncDetails ? "rotate-180" : ""}`} />
      </button>

      {showSyncDetails && (
        <div className="mt-4 space-y-2 rounded-lg bg-gray-50 p-3 text-sm">
          <div className="flex justify-between gap-3">
            <span className="text-gray-500">Modo</span>
            <span className="text-right font-medium text-gray-900">
              {isUsdConversion ? "Convertir a USD" : "Moneda del embudo"}
            </span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-gray-500">Orden original</span>
            <span className="text-right font-medium text-gray-900">
              {formatCurrency(Number(orderDetails.shopify_original_total ?? orderDetails.total_value_with_discount ?? 0), originalSymbol)} {originalCurrency}
            </span>
          </div>
          {hasAppliedUsdRate && (
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">Tasa usada</span>
              <span className="text-right font-medium text-gray-900">
                {`1 ${originalCurrency} = ${Number(orderDetails.shopify_rate_used).toFixed(6)} USD`}
              </span>
            </div>
          )}
          {hasAppliedUsdRate && orderDetails.shopify_rate_date && (
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">Fecha de tasa</span>
              <span className="text-right font-medium text-gray-900">{orderDetails.shopify_rate_date}</span>
            </div>
          )}
          {state === ShopifyState.SUCCESS && syncUpdatedAt && (
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">Sincronizada</span>
              <span className="text-right font-medium text-gray-900">{formatDateInZone(syncUpdatedAt, timezone)}</span>
            </div>
          )}
          {state === ShopifyState.SUCCESS && orderDetails.shopify_total_sent != null && (
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">Enviado a Shopify</span>
              <span className="text-right font-medium text-gray-900">
                {`${formatCurrency(Number(orderDetails.shopify_total_sent), sentSymbol)} ${sentCurrency}`}
              </span>
            </div>
          )}
          {orderDetails.shopify_retry_count != null && orderDetails.shopify_retry_count > 0 && (
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">Intentos</span>
              <span className="text-right font-medium text-gray-900">{orderDetails.shopify_retry_count}</span>
            </div>
          )}
          {orderDetails.shopify_next_retry_at && (
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">Próximo intento</span>
              <span className="text-right font-medium text-gray-900">
                {formatDateInZone(orderDetails.shopify_next_retry_at, timezone)}
              </span>
            </div>
          )}
          {orderDetails.shopify_last_error_code && (
            <div className="flex justify-between gap-3">
              <span className="text-gray-500">Código</span>
              <span className="text-right font-medium text-gray-900">{orderDetails.shopify_last_error_code}</span>
            </div>
          )}
        </div>
      )}

      {canRetry && (
        <button
          type="button"
          onClick={handleRetryShopify}
          disabled={isRetrying}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#4368E5] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#3154c9] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`} />
          {isRetrying ? "Reintentando Shopify" : "Reintentar sincronización"}
        </button>
      )}
    </div>
  );
};

const OrderDetailsContent = () => {
  const { language, timezone } = useAuth();
  const {
    products,
    customer,
    paymentDetails,
    shippingAddress,
    orderDetails,
    loading,
    error,
    setOrderDetailsData,
    setLoadingState,
    setErrorState,
  } = useOrderDetails();

  const { hash_id } = useParams();
  const dataLoadedRef = useRef(false);
  const [errorOrderDetails, setErrorOrderDetails] = useState<string>();
  const [confirmationInfo, setConfirmationInfo] = useState<ConfirmationInfo | null>(null);

  const customFields = customer?.custom_fields ?? [];
  const normalizeKey = (k: unknown) => String(k ?? "").trim().toLowerCase();
  const isZipKey = (key: unknown) => ["zip", "zip code", "postal_code", "postal code", "codigo postal", "código postal"].includes(normalizeKey(key));
  const zipCodeField = customFields.find((f) => isZipKey(f?.key));
  const zipCode = zipCodeField?.value ? String(zipCodeField.value) : undefined;
  const customFieldsWithoutZip = customFields.filter((f) => !isZipKey(f?.key));

  const fetchOrderDetails = useCallback(async () => {
    if (!hash_id) return;
    setLoadingState(true);
    setErrorOrderDetails(undefined);
    const { res, error } = await handleApi(getOrderDetails(hash_id), false);

    if (error) {
      setErrorState("Failed to load order details");
      setLoadingState(false);
      return;
    }

    if (res) {
      if (res.order?.dropi_integration_error) {
        setErrorOrderDetails(res.dropi_integration_error || res.order.dropi_integration_error);
      }
      setOrderDetailsData(res);
      setLoadingState(false);
      dataLoadedRef.current = true;
    }
  }, [hash_id, setErrorState, setLoadingState, setOrderDetailsData]);

  useEffect(() => {
    if (dataLoadedRef.current || !hash_id) {
      if (!hash_id) {
        setErrorState("No order ID provided");
      }
      return;
    }
    fetchOrderDetails();
  }, [hash_id, fetchOrderDetails, setErrorState]);

  useEffect(() => {
    if (!orderDetails?.id) return;
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId) return;
    const fetchConfirmation = async () => {
      const { res, error: err } = await handleApi(getConfirmationStatuses(ownerId, [orderDetails.id]), false);
      if (!err && res) {
        const info = res[String(orderDetails.id)];
        if (info) setConfirmationInfo(info);
      }
    };
    fetchConfirmation();
  }, [orderDetails?.id]);

  const handleAddressUpdated = () => {
    dataLoadedRef.current = false;
    fetchOrderDetails();
  };

  const isDropiRetryable =
    orderDetails?.dropi_state === DropiState.ERROR ||
    orderDetails?.dropi_state === DropiState.PENDING_SYNC;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      <TopNavBarComponent />
      <div className="flex flex-row md:flex-row">
        {/* Main content column - takes up 70% of the space */}
        <div className="m-6 md:w-[70%]">
          {orderDetails && (
            <ProductDetail order={orderDetails} products={products} />
          )}
        </div>

        {/* Sidebar column - takes up 30% of the space */}
        <div className="m-6 md:w-[30%] space-y-4 flex flex-col gap-4">
          {customer && <CustomerCard customer={customer} />}
          {paymentDetails && (
            <PaymentDetailsCard paymentDetails={paymentDetails} />
          )}
          {shippingAddress && (
            <ShippingCard
              shippingAddress={shippingAddress}
              zipCode={zipCode}
              confirmationId={confirmationInfo?.confirmation_id}
              aiEnabled={confirmationInfo?.ai_enabled}
              onAddressUpdated={handleAddressUpdated}
            />
          )}
          {customFieldsWithoutZip.length > 0 && (
            <CustomerFieldsCard customFields={customFieldsWithoutZip} />
          )}

          {orderDetails?.dropi_state === DropiState.SUCCESS &&
            orderDetails?.dropi_url && (
              <IntegrationCardInfo
                title={dictionary["integration-dropi"][language]}
                description={
                  dictionary["integration-dropi-description-success"][language]
                }
                buttonText={
                  dictionary["integration-dropi-button-text"][language]
                }
                buttonLink={orderDetails?.dropi_url}
              />
            )}
          {isDropiRetryable && orderDetails && customer && (
            <DropiRetryCard
              orderId={orderDetails.id}
              dropiState={orderDetails.dropi_state}
              customer={customer}
              error={errorOrderDetails}
              onSynced={handleAddressUpdated}
            />
          )}
          <ShopifySyncCard timezone={timezone} onRetryComplete={fetchOrderDetails} />
        </div>
      </div>
    </div>
  );
};

export const OrderDetailsPage = () => {
  const { hash_id } = useParams();

  return (
    <OrderDetailsProvider hashId={hash_id}>
      <OrderDetailsContent />
    </OrderDetailsProvider>
  );
};
